import React, {PureComponent, ReactNode, ReactElement} from "react"
import {RouteComponentProps, withRouter} from "react-router-dom"

import authentication from "@irati/auth"
import {UserList, Team} from "@irati/types"
import UsersForBusiness, {userList, getNextPage, UsersForBusinessVariables} from "@irati/identity/apollo/query/UsersForBusiness"
import adminTracking from "@irati-tracking/admin"
import FormattedMessage from "@irati/utils/lang"
import History from "@irati/utils/history"

import AlphabetSearch, {onAlphabet} from "../AlphabetSearch/AlphabetSearch"
import SearchAndSort, {onSearch, onSort, onFilter} from "../SearchAndSort"
import NoUsers from "../NoUsers/NoUsers"
import {A_Z} from "../../constants/sort"
import {FILTER_OPTIONS_NO_DEACTIVATED, FILTER_OPTIONS_WITH_MANAGERS} from "../../constants/filter"
import SearchAndSortBusinessUnit from "../SearchAndSortBusinessUnit"
import NoManagers from "../NoManagers"
import FilterEsdAndDob from "../FilterEsdAndDob"
import ShowMore from "./ShowMore"

export const INITIAL_USERLIST_VARIABLES = (
  teamId: string | null,
  hasEmploymentStartDate?: boolean,
  hasDateOfBirth?: boolean
): UsersForBusinessVariables => ({
  includeBusiness: false,
  hasEmploymentStartDate,
  hasDateOfBirth,
  enabled: true,
  limit: 10,
  offset: 0,
  businessId: authentication.business().uuid,
  teamId,
  sort: A_Z
})

interface Props extends RouteComponentProps<{}, {}, {toggleDatesFilter?: boolean}> {
  teamId: string | null
  protectedTeams: Team[]
  businessUnits?: Team[]
  filterOptions?: any[]
  children: (props: {users: UserList; variables: UsersForBusinessVariables}) => ReactNode
  refetchOnMount: boolean
  filterToggle: boolean
  onUpdateVariables?: () => void
}

interface State {
  variables: UsersForBusinessVariables
  viewingText: ReactElement | null
  useLoader: boolean
  checkEsd: boolean
  checkDob: boolean
}

class UsersList extends PureComponent<Props, State> {
  static defaultProps: Partial<Props> = {
    filterOptions: FILTER_OPTIONS_NO_DEACTIVATED,
    refetchOnMount: true
  }

  constructor(props: Props) {
    super(props)
    const toggleDatesFilter = props?.location?.state?.toggleDatesFilter
    const variables = INITIAL_USERLIST_VARIABLES(
      props.teamId,
      toggleDatesFilter ? false : undefined,
      toggleDatesFilter ? false : undefined
    )
    this.state = {
      variables,
      viewingText: null,
      useLoader: true,
      checkEsd: toggleDatesFilter || false,
      checkDob: toggleDatesFilter || false
    }
    this.setVariables(variables)
  }

  componentDidMount() {
    if (this.props.refetchOnMount) {
      this.refetch()
    }
    if (this.props?.location?.state?.toggleDatesFilter) {
      History.replace({pathname: location.pathname, state: {}})
    }
  }

  /** @description: Fallback function until `<UsersForBusiness />` Query component mounts */
  refetch = () => {}

  setVariables = (variables: UsersForBusinessVariables) => {
    const {onUpdateVariables} = this.props
    if (onUpdateVariables) {
      onUpdateVariables(variables)
    }
    this.setState({variables, useLoader: true})
  }

  handleSearch: onSearch = (searchString) => {
    adminTracking.searchPeople()
    this.setVariables({...this.state.variables, searchString})
  }

  handleFilter: onFilter = (filter) => {
    const variables = {
      ...INITIAL_USERLIST_VARIABLES(this.props.teamId, this.state.variables.hasEmploymentStartDate, this.state.variables.hasDateOfBirth),
      ...filter,
      searchString: this.state.variables.searchString
    }
    this.setVariables(variables)
  }

  handleSort: onSort = (sort) => {
    this.setVariables({...this.state.variables, sort, offset: 0})
  }

  handleBusinessUnit = (businessUnitId: string) => {
    const variables = {
      ...INITIAL_USERLIST_VARIABLES(businessUnitId, this.state.variables.hasEmploymentStartDate, this.state.variables.hasDateOfBirth),
      searchString: this.state.variables.searchString
    }
    this.setVariables(variables)
  }

  handleAlphabet: onAlphabet = (lastNameCharacter) => {
    const variables = {
      ...INITIAL_USERLIST_VARIABLES(this.props.teamId, this.state.variables.hasEmploymentStartDate, this.state.variables.hasDateOfBirth),
      lastNameCharacter
    }
    this.setVariables(variables)
  }

  handleCompleted = (data) => {
    const totalUsers = data?.usersForBusiness?.total
    const currentUsersInView = data?.usersForBusiness?.results.length

    const state: Pick<State, "useLoader" | "viewingText"> = {useLoader: false, viewingText: null}

    if (totalUsers && currentUsersInView) {
      state.viewingText = <FormattedMessage id="admin.containers.teamUserList.viewingText" values={{currentUsersInView, totalUsers}} />
    }

    this.setState(state)
  }

  handleChangeCheckEsd = () => {
    this.setVariables({
      ...this.state.variables,
      hasEmploymentStartDate: this.state.variables.hasEmploymentStartDate === undefined ? false : undefined
    })
    this.setState({checkEsd: !this.state.checkEsd})
  }

  handleChangeCheckDob = () => {
    this.setVariables({
      ...this.state.variables,
      hasDateOfBirth: this.state.variables.hasDateOfBirth === undefined ? false : undefined
    })
    this.setState({checkDob: !this.state.checkDob})
  }

  render() {
    const {variables, viewingText, useLoader, checkDob, checkEsd} = this.state
    const {children, filterOptions, protectedTeams, businessUnits, teamId, filterToggle} = this.props
    const message = teamId && !variables.searchString && !variables.lastNameCharacter && "admin.containers.teamUserList.noUsers.message"

    const businessUnit = businessUnits?.find((t) => t.uuid === teamId)

    return (
      <>
        {businessUnits?.length ? (
          <SearchAndSortBusinessUnit
            businessUnits={businessUnits}
            businessUnitId={businessUnit?.uuid}
            filterOptions={filterOptions}
            variables={variables}
            onSearch={this.handleSearch}
            onFilter={this.handleFilter}
            onBusinessUnitUpdate={this.handleBusinessUnit}
            onSort={this.handleSort}
            viewingText={viewingText}
            filterToggle={filterToggle}
            protectedTeams={protectedTeams}>
            <FilterEsdAndDob
              checkEsd={checkEsd}
              checkDob={checkDob}
              onEsdChange={this.handleChangeCheckEsd}
              onDobChange={this.handleChangeCheckDob}
            />
          </SearchAndSortBusinessUnit>
        ) : (
          <SearchAndSort
            filterOptions={teamId ? FILTER_OPTIONS_WITH_MANAGERS : filterOptions}
            variables={variables}
            onSearch={this.handleSearch}
            onFilter={this.handleFilter}
            onSort={this.handleSort}
            viewingText={viewingText}
            filterToggle={filterToggle}
            protectedTeams={protectedTeams}>
            <FilterEsdAndDob
              checkEsd={checkEsd}
              checkDob={checkDob}
              onEsdChange={this.handleChangeCheckEsd}
              onDobChange={this.handleChangeCheckDob}
            />
          </SearchAndSort>
        )}
        <AlphabetSearch onSearch={this.handleAlphabet} variables={variables} />
        <UsersForBusiness variables={variables} useLoader={useLoader} notifyOnNetworkStatusChange onCompleted={this.handleCompleted}>
          {(apollo) => {
            this.refetch = apollo.refetch
            const users = userList(apollo)
            if (users.results.length === 0) {
              if (variables.managersOnly) {
                return <NoManagers />
              }
              return <NoUsers message={message} searchTerm={variables.searchString} />
            }
            return (
              <>
                {children({variables, users})}
                <ShowMore onNewPage={(isVisible) => isVisible && getNextPage(apollo, variables)} />
              </>
            )
          }}
        </UsersForBusiness>
      </>
    )
  }
}

export default withRouter(UsersList)
